import React, { useContext } from "react";
import { ThemeContext } from "../../Hooks/ThemeContext";
import { LanguageContext } from "../../Hooks/LanguageContext";
import { TEXTS } from "../../Hooks/Languages";
import "./About.css";
import CollapsibleText from "../../Components/CollapsibleText/CollapsibleText";
import TeamMember from "../../Components/TeamMember/TeamMember";
import aboutPoster from "../../Assets/aboutPagePoster.png";
import aboutPosterdark from "../../Assets/aboutPagePosterDark.png";
import ani from "../../Assets/ani.png";
import teo from "../../Assets/teo.png";
import nino from "../../Assets/nino.png";
import mari from "../../Assets/mari.png";
import { Helmet } from "react-helmet-async";

const About = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  return (
    <div id="aboutPage" className={`aboutMainCont ${theme}`}>
      <Helmet>
        <title>{TEXTS[language].aboutTitle}</title>
        <meta name="description" content={TEXTS[language].aboutDescription} />
        <meta name="keywords" content={TEXTS[language].aboutKeywords} />
        <meta name="author" content="BestSoft Team" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://bestsoft.ge/about" />
        
        {/* Open Graph მეტა თაგები */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TEXTS[language].aboutTitle} />
        <meta property="og:description" content={TEXTS[language].aboutDescription} />
        <meta property="og:url" content="https://bestsoft.ge/about" />
        <meta property="og:site_name" content="BestSoft" />
        <meta property="og:image" content="https://bestsoft.ge/logo192.png" />
        <meta property="og:locale" content={language === 'ge' ? 'ka_GE' : 'en_US'} />
        
        {/* Twitter Card მეტა თაგები */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TEXTS[language].aboutTitle} />
        <meta name="twitter:description" content={TEXTS[language].aboutDescription} />
        <meta name="twitter:image" content="https://bestsoft.ge/logo192.png" />
        
        {/* Geo meta tags */}
        <meta name="geo.region" content="GE-TB" />
        <meta name="geo.placename" content="Tbilisi, Georgia" />
        <meta name="geo.position" content="41.7151;44.8271" />
        <meta name="ICBM" content="41.7151, 44.8271" />
        
        {/* Structured Data for About Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": TEXTS[language].aboutTitle,
            "description": TEXTS[language].aboutDescription,
            "url": "https://bestsoft.ge/about",
            "mainEntity": {
              "@type": "Organization",
              "name": "BestSoft",
              "description": TEXTS[language].aboutDescription,
              "foundingDate": "2023",
              "numberOfEmployees": "4",
              "founder": [
                {
                  "@type": "Person",
                  "name": "Nino Barjadze"
                },
                {
                  "@type": "Person", 
                  "name": "Ani Beroshvili"
                },
                {
                  "@type": "Person",
                  "name": "Teona Kartvelishvili"
                },
                {
                  "@type": "Person",
                  "name": "Mariam Lavrelashvili"
                }
              ],
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "GE",
                "addressLocality": "Tbilisi"
              }
            }
          })}
        </script>
        
        <meta name="facebook-domain-verification" content="ghhck2ke4iia07bma9rr2q7zny6r10" />
      </Helmet>
      <div className={`aboutConts ${theme}`}>
        <div className={`aboutCont1 ${theme}`}>
          <img
            alt="About BestSoft poster  BestSoft-ის შესახებ პოსტერის სურათი"
            src={theme === "dark" ? aboutPosterdark : aboutPoster}
            loading="lazy"
            className="logo"
          />
        </div>
        <div className={`aboutCont2 ${theme}`}>
          <h1 className="colorPink">{TEXTS[language].aboutUs}</h1>
          <CollapsibleText text={TEXTS[language].aboutP1} />
        </div>

        <div className={`aboutCont3 ${theme}`}>
          <h1 className="colorPink">{TEXTS[language].mission}</h1>
          <CollapsibleText text={TEXTS[language].vision} />
          <CollapsibleText text={TEXTS[language].mission2} />
        </div>

        <div className={`members ${theme}`}>
          <h1 id="aboutUsMainH1"> {TEXTS[language].meetTeam}</h1>
          <CollapsibleText text={TEXTS[language].team} />

          <TeamMember
            name={TEXTS[language].names.ani}
            title="Full-stack Developer"
            bio={TEXTS[language].teamMembers.ani}
            image={ani}
            portfolioLink="https://ani-beroshvili.vercel.app/portfolio"
            contactLink="tel:+995577300480"
            socialMediaLinks={[
              "https://facebook.com/anano303",
              "https://instagram.com/anano303",
              "https://www.linkedin.com/in/ani-beroshvili-7a8998214?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
            ]}
            className={`member ${theme}`} // Custom className
            id="ani" // Custom ID
          />
          <TeamMember
            name={TEXTS[language].names.teo}
            title="Full-stack Developer"
            bio={TEXTS[language].teamMembers.teo}
            image={teo}
            portfolioLink="https://teona-kartvelishvili.vercel.app/"
            contactLink="tel:+995551999055"
            socialMediaLinks={[
              "https://www.facebook.com/teo.qartvel",
              "https://www.instagram.com/teo.kartvel?igsh=dnUzODMxcnZzN3p5&utm_source=qr",
              "https://www.linkedin.com/in/teona-kartvelishvili-8a4005257",
            ]}
            className={`member ${theme}`} // Custom className
            id="teo" // Custom ID
          />
          <TeamMember
            name={TEXTS[language].names.nino}
            title="UX/UI Designer, Graphic Designer"
            bio={TEXTS[language].teamMembers.nino}
            image={nino}
            portfolioLink="https://www.behance.net/ninobarjadze1"
            contactLink="https://nino-contact.com"
            socialMediaLinks={[
              "https://www.facebook.com/ninuca.barjadze/",
              "https://www.instagram.com/nin.illustrations/?igsh=bzk0c2Q4eXc1MTE3&utm_source=qr",
              "https://www.linkedin.com/in/ninobarjadze",
            ]}
            className={`member ${theme}`} // Custom className ...
            id="nino" // Custom ID
          />
          <TeamMember
            name={TEXTS[language].names.mari}
            title="UX/UI Designer, Graphic Designer"
            bio={TEXTS[language].teamMembers.mari}
            image={mari}
            portfolioLink="https://www.instagram.com/"
            contactLink="https://mari-contact.com"
            socialMediaLinks={[
              "https://www.facebook.com/lavrelashvilimari",
              "https://www.instagram.com/mariamlavrelashvili/profilecard/?igsh=MXRpM3p4cXB6ZWhsYw==",
              "https://linkedin.com",
            ]}
            className={`member ${theme}`} // Custom className
            id="mari" // Custom ID
          />
          {/* Repeat for Teona, ani and Mari with different socialLinks */}
        </div>
      </div>
    </div>
  );
};

export default About;
